import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/App";
import { toast } from "sonner";
import { Users, Download, Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";

/** AdminLeadsPage — every captured email (diagnostic funnel + referrals). CSV export
 *  means the owner always owns the list, independent of any email provider. */
const AdminLeadsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads");
      setData(r);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading leads…
      </div>
    );
  }

  const leads = data?.leads || [];

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-leads-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every email captured by the diagnostic funnel and referral invites. Synced leads land in
            your Resend "PassADC Leads" audience — send campaigns from the Resend dashboard
            (Broadcasts), or export the CSV to use anywhere.
          </p>
        </div>
        <a
          href={`${API}/admin/leads/export.csv`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90"
          data-testid="leads-export-btn"
        >
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total leads", value: data?.total ?? 0 },
          { label: "From diagnostic", value: data?.diagnostic_count ?? 0 },
          { label: "From referrals", value: data?.referral_count ?? 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {leads.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No leads yet — they'll appear here as people finish the public diagnostic or get referred.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">Email</th>
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium">Detail</th>
                <th className="px-4 py-2.5 font-medium">Synced</th>
                <th className="px-4 py-2.5 font-medium">Converted</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={`${l.email}-${i}`} className="border-t border-border" data-testid={`lead-row-${i}`}>
                  <td className="px-4 py-2.5 font-medium">{l.email}</td>
                  <td className="px-4 py-2.5">{l.name || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.source === "diagnostic" ? "bg-blue-500/15 text-blue-600" : "bg-purple-500/15 text-purple-600"}`}>
                      {l.source}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{l.detail || "—"}</td>
                  <td className="px-4 py-2.5">{l.synced_to_resend ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-muted-foreground/50" />}</td>
                  <td className="px-4 py-2.5">{l.converted ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminLeadsPage;
